<template>
  <crud
    ref="crud"
    :items="items"
    :fields="authorizedFields"
    :editable="processRoleArg(editable)"
    :deletable="processRoleArg(deletable)"
    :refreshable="refreshable"
    :paginating="paginating"
    :perPage="perPage"
    :filtering="filtering"
    :bulk="bulk"
    :actions="actions"
    :exportable="exportable"
    :creatable="processRoleArg(creatable)"
    :form-component="formComponent"
    :form-order="formOrder"
    :total="total"
    :loading="isBusy"
    :serverside="true"
    :requestAllItems="requestAllItems"
    :form-error="formError"
    :allItemsActions="allItemsActions"
    @page-changed="changePage"
    @sort-changed="changeSort"
    @save="saveItem"
    @cancel="cancelItem"
    @remove="removeItems"
    @filters-changed="changeFilters"
    @refresh="refresh"
  >
    <template v-for="(slot, index) in Object.keys($scopedSlots)" :slot="slot" slot-scope="row">
      <td :key="slot">
        <slot :name="slot" :item="row.item" :field="row.field" :value="row.value"></slot>
      </td>
    </template>
  </crud>
</template>

<script>
import Crud from './Crud/Crud'
import moment from 'moment'

import { EventBus } from '@/utils/Global'

// @TODO: Maybe use mixins for crud?

export default {
  props: {
    model: Object,
    extended: Boolean,
    fields: Object,
    actions: {
      type: Array,
      default: () => []
    },
    editable: {
      default: true
    },
    deletable: {
      default: true
    },
    refreshable: {
      type: Boolean,
      default: true
    },
    paginating: {
      type: Boolean,
      default: true
    },
    filtering: {
      type: Boolean,
      default: true
    },
    bulk: {
      type: Boolean,
      default: true
    },
    perPage: {
      type: Number,
      default: 25
    },
    nameField: {
      type: String,
      default: 'name'
    },
    include: {
      type: Array,
      default: () => []
    },
    fetchFilters: {
      type: Object,
      default: () => {}
    },
    exportable: {
      type: Boolean,
      default: true
    },
    creatable: {
      default: true
    },
    formComponent: {
      type: Object,
      default: null
    },
    formOrder: {
      type: Array,
      default: null
    },
    preSave: {
      type: Function,
      default: null
    },
    atferSave: {
      type: Function,
      default: null
    },
    defaultOrder: {
      type: String,
      default: null
    },
    defaultOrderAsc: {
      type: Boolean,
      default: true
    },
    allItemsActions: {
      type: Array,
      default: () => []
    }
  },
  created () {
    this.refresh()
  },
  beforeDestroy () {
    EventBus.$off('refresh', this.refreshHandler)
  },
  data () {
    let refreshHandler = () => this.refresh()

    EventBus.$on('refresh', refreshHandler)

    return {
      items: [],
      isBusy: true,
      page: 1,
      total: 0,
      order: this.defaultOrder || Object.keys(this.fields)[0],
      orderAsc: this.defaultOrderAsc,
      filters: null,
      previousFilters: null,
      formError: null,
      refreshHandler,
      errorTranslations: [
        {
          rule: 'nesmí být null',
          message: () => this.$t('errors.notEmpty')
        },
        {
          rule: /velikost musí být mezi ([0-9]+) a [0-9]{5,}/,
          message: (match) => this.$t('errors.mustBeLonger', [match[1]])
        }
      ]
    }
  },
  watch: {
    fetchFilters: 'refresh'
  },
  methods: {
    processRoleArg (arg) {
      if (arg === true || arg === false) return arg
      if (Array.isArray(arg)) {
        return !!arg.find(role => this.$auth.hasRole(role))
      }
      return true
    },

    refresh () {
      this.isBusy = true
      this.model.getList(this.prepareParams())
        .then(result => {
          this.items = result.items

          // Apply preprocessing if needed
          this.items.forEach(item => {
            Object.keys(this.fields).forEach(field => {
              let options = this.fields[field]
              if (options.preProcess) {
                item[field] = options.preProcess.apply(this, [item[field], item, field])
              }
            })
          })

          this.total = result._page.totalElements
          this.isBusy = false
        })
    },

    changePage (page) {
      this.page = page
      this.refresh()
    },

    changeSort (sort, asc) {
      this.order = sort
      this.orderAsc = asc
      this.refresh()
    },

    saveItem (item, fields) {
      this.formError = null

      // Only save editable items
      let updatedItem = Object.keys(item).reduce((result, key) => {
        if (key.charAt(0) === '_' || fields.indexOf(key) >= 0) {
          result[key] = item[key]
        }
        return result
      }, {})

      this.doSave(updatedItem, fields)
        .catch(error => {
          // Reset form
          if (this.$refs.crud) {
            this.$refs.crud.saveFailed(item, fields)
          }

          let showError = false

          // If server response contains info about invalid fields
          if (error.response &&
            error.response.data &&
            error.response.data.errors &&
            error.response.status === 400
          ) {
            // Populate errors with server response
            error.response.data.errors.forEach(item => {
              if (item.property && this.authorizedFields[item.property] && this.processRoleArg(this.authorizedFields[item.property].editable)) {
                // Try to load more user friendly message
                let message = this.errorTranslations
                  .reduce((message, item) => {
                    if (typeof item.rule === 'object') {
                      let match = message.match(item.rule)
                      if (match) {
                        return message.replace(item.rule, typeof item.message === 'function' ? item.message(match) : item.message)
                      }
                    } else {
                      if (message === item.rule) {
                        return item.message
                      }
                    }
                    return message
                  }, item.message)

                this.fields[item.property].errors.push(message.charAt(0).toUpperCase() + message.slice(1))
              } else if (item.property) {
                showError = true
              }
            })
          }

          // Default error handler
          console.log(error)

          if (showError) {
            this.formError = `${this.$t('savingFailed')}. ${error}`
          }
        })
    },

    doSave (item, fields) {
      if (this.preSave) {
        this.preSave(item, fields)
      }

      // Create item copy
      item = { ...item }

      // Reset errors
      Object.keys(this.fields).forEach(field => {
        if (this.fields[field].errors) {
          this.fields[field].errors.splice(0, this.fields[field].errors.length)
        }
      })

      // Apply post processing if needed
      Object.keys(this.fields).forEach(field => {
        let options = this.fields[field]
        if (options.postProcess) {
          item[field] = options.postProcess.apply(this, [item[field], item, field])
        }
      })

      if (item._links) {
        let updated = Object.keys(item).reduce((result, field) => {
          // Only allow editable or internal fields
          /*
          if (field.charAt(0) === '_' ||
            (this.authorizedFields[field] !== undefined &&
              (this.authorizedFields[field].editable ||
              this.authorizedFields[field].editable === undefined)
            )
          ) {
            result[field] = item[field]
          }
          */

          if (field.charAt(0) === '_' ||
            (this.fields[field] && this.fields[field].type !== 'reference' && this.fields[field].type !== 'references') ||
            (this.authorizedFields[field] !== undefined &&
              (this.authorizedFields[field].editable ||
              this.authorizedFields[field].editable === undefined)
            )
          ) {
            result[field] = item[field]
          }

          return result
        }, {})

        return this.model
          .update(updated)
          .then(result => {
            if (this.$refs.crud) {
              this.$refs.crud.saved(item, fields)
            }
            if (this.atferSave) {
              return this.atferSave(result, item, fields)
            }
          })
      } else {
        return this.model
          .create(item)
          .then(result => {
            if (this.$refs.crud) {
              this.$refs.crud.saved(item, fields)
            }
            this.refresh()
            if (this.atferSave) {
              return this.atferSave(result, item, fields)
            }
          })
      }
    },

    removeItems (items) {
      if (confirm(`Opravdu chcete smazat ${items.map(item => item[this.nameField]).join(', ')}?`)) {
        items.forEach(item => {
          let removed = { ...item }

          Object.keys(this.fields).forEach(field => {
            let options = this.fields[field]
            if (options.postProcess) {
              removed[field] = options.postProcess.apply(this, [removed[field], removed, field])
            }
          })

          this.model.delete(removed)

          let index = this.items.findIndex(sub => sub._self === removed._self)
          if (index >= 0) {
            this.items.splice(index, 1)
          } else {
            console.warn('Failed to remove item', removed, this.items)
          }
        })
      }
    },

    changeFilters (filters) {
      this.filters = filters

      if (this.$refs.crud && this.previousFilters !== JSON.stringify(this.filters)) {
        this.previousFilters = JSON.stringify(this.filters)
        if (this.page !== 1) {
          this.$refs.crud.changePage(1)
        } else {
          this.refresh()
        }
      }
    },

    mapToIds (items) {
      return items.map(item => {
        return item._self.split('/').filter(item => item.length > 0).pop()
      })
    },

    parseRangeDate (input, def) {
      if (input.length === 0) {
        return def || 0
      } else {
        return Math.round(moment(input).toDate().getTime())
      }
    },

    prepareParams () {
      let params = {
        include: this.include,
        filters: this.fetchFilters || {},
        page: this.page - 1,
        size: this.perPage
      }

      // Make sure we have a copy of fetchFilters
      params.filters = { ...params.filters }

      if (this.order) {
        params.sort = this.order + ',' + (this.orderAsc ? 'asc' : 'desc')
      }

      if (this.extended) {
        params.projection = 'extended'
      }

      // If there are any filters defined
      if (this.filters) {
        for (let field in this.filters) {
          // Load value and field options
          let options = this.authorizedFields[field]
          let value = this.filters[field]

          // If it's authorized field and has filter value
          if (options && value !== null && value !== undefined && value.length !== 0) {
            // Classic text parameter
            if (options.type === undefined || options.type === 'text') {
              params.filters[field] = '[LIKE]' + this.filters[field]
            }

            // Date range
            if (options.type === 'date') {
              let values = value.split('|')
              let from = this.parseRangeDate(values[0])
              let to = this.parseRangeDate(values[1], new Date().getTime())

              params.filters[field] = `[BETWEEN]${from}-${to}`
            }

            // Enum values are mapped as _self
            if (options.type === 'enum') {
              params.filters[field] = '[IN]' + value.map(item => item._self).join(',')
            }

            // We need to extract id for references
            if (options.type === 'reference' || options.type === 'references') {
              params.filters[field + '.id'] = '[IN]' + this.mapToIds(value).join(',')
            }

            if (options.type === 'bool' && value !== undefined) {
              params.filters[field] = value ? 'true' : 'false'
            }
          }
        }
      }

      return params
    },

    requestAllItems (filters) {
      let params = this.prepareParams()
      params.size = 99999999
      params.page = 0

      this.isBusy = true
      return this.model.getList(params)
        .then(result => {
          this.isBusy = false
          return result.items
        })
    },

    cancelItem () {
      this.formError = null
    }
  },
  computed: {
    authorizedFields () {
      return Object.keys(this.fields)
        .reduce((fields, field) => {
          let options = this.fields[field]

          if (options.editable && Array.isArray(options.editable)) {
            options.editable = this.$auth.hasRole(options.editable)
          }

          if (!options.roles && !options.role) {
            fields[field] = options
          }

          if (options.role && this.$auth.hasRole(options.role)) {
            fields[field] = options
          }

          if (options.roles && this.$auth.hasRole(options.roles)) {
            fields[field] = options
          }

          return fields
        }, {})
    }
  },
  components: {
    Crud
  }
}
</script>

<style>

</style>



// WEBPACK FOOTER //
// ModelCrud.vue?2f4f3c80